import React, { memo, useCallback, useEffect, useState } from "react";
import { FaCopy, FaFloppyDisk, FaPaperPlane } from "react-icons/fa6";
import { v4 as uuidv4 } from "uuid";
import "./invoice-form.style.scss";
import "../form-inputs/form-inputs.style.scss";
import { formatEnDate } from "../../global/date.util";

// form handle
import { useFormik, FormikErrors } from "formik";
import * as yup from "yup";

// components
import InputDatePicker from "../input-date-picker/input-date-picker.component";
import MultipleTagsInput from "../form-inputs/multiple-tags-input.component";
import SelectBox from "../form-inputs/select-box.component";
import Spinner from "../spinner/spinner.component";
import InvoiceItemsFooter from "./invoice-items-footer.component";
import CustomerSelector from "./customer-selector.component";
import InlineNotice from "../inline-notice/inline-notice.component";

// global functions and hook
import strings from "../../global/strings";
import { isEmptyObject } from "../../global/function";
import { PHONE_REGEX, PHONE_REGEX_MESSAGE } from "../../global/phone.const";
import { useBillTotals } from "../../hooks/useBillTotals";
import type { InvoiceFormValues } from "./invoice-form.types";
import { sanitizeDraftPayload } from "./invoice-form.util";

// queries
import { useCompanyDetails } from "../../queries/company.queries";

import { useLocalDrafts } from "../../context/local-drafts.context";
import { useIrdStatus } from "../../hooks/useIrdStatus";

interface InvoiceFormProps {
	formType: "individual" | "bulk";
	draftSave: (
		draft: Partial<InvoiceFormValues> & Record<string, unknown>,
		clickedButton: string
	) => Promise<unknown>;
	currentDraft: (Partial<InvoiceFormValues> & Record<string, unknown>) | null;
	onSetCurrent: (draft: Partial<InvoiceFormValues>) => void;
	onUnsavedChanges: (
		values: Partial<InvoiceFormValues> | Record<string, never>
	) => void;
}

// Static template for a blank form, used as the reset-effect's base spread
// below. Deliberately NOT read via `formik.initialValues` there: Formik
// mutates its own `initialValues` ref to whatever draft was last loaded
// (via resetForm), so by the time a *different* draft is switched to,
// `formik.initialValues` reflects the *previous* draft, not a blank slate.
// Spreading that stale object first meant identity fields the new draft
// intentionally omits (e.g. invoice-form's Duplicate strips draft_id/
// draft_bill_status) got silently refilled from the draft being duplicated,
// so saving the "new" draft actually overwrote the original's DB row.
const BLANK_INVOICE_VALUES: InvoiceFormValues = {
	bill_for: "",
	buyer_pan: "",
	buyer_name: "",
	company_customer_id: null,
	customer_info: null,
	customer_name: "",
	customer_gender: "",
	customer_pan_vat: "",
	customer_address: "",
	customer_city: "",
	customer_email: "",
	customer_mobile: "",
	customer_type: "individual",
	total_sales: 0,
	invoice_number: "",
	invoice_date: formatEnDate(new Date(), "YYYY-MM-DD"),
	sales_date: formatEnDate(new Date(), "YYYY-MM-DD"),
	payment_mode: "credit",
	sub_total: 0,
	non_taxable_total_sales: 0,
	taxable_total_sales: 0,
	non_taxable_amt: 0,
	taxable_sales_vat: 0,
	vat: 0,
	non_taxable_service_charge: "",
	taxable_service_charge: "",
	amount_for_esf: 0,
	esf: 0,
	non_taxable_discount: "",
	taxable_discount: "",
	draft_valid: "",
	sale_info: "",
	billed_items: [],
	clickedButton: "draft",
};

function InvoiceForm(props: InvoiceFormProps) {
	// irdStatus (bill/IRD server reachability) is read via useIrdStatus(),
	// which dedupes this query+mutation pair across all its consumers.
	const { data: companyData } = useCompanyDetails();
	const preferenceDetails = companyData?.company?.preference;
	const companyDetails = companyData?.company;
	const { irdStatus, irdStatusLoading, checkStatus } = useIrdStatus();

	const [isCustomerViewOpen, setIsCustomerViewOpen] = useState(false);

	// customer tags and bill for to store temporarily
	const [tags, setTags] = useState<string[]>([]);

	const { addLocalDraft } = useLocalDrafts();

	function convertTagToString(array: string[]): string {
		const string = "#" + array.join("#");
		return string;
	}

	function convertTagToArray(value?: string): string[] {
		const array = value?.split("#").filter(Boolean);
		return array || [];
	}

	const invoiceSchema = yup.lazy((values: Partial<InvoiceFormValues>) => {
		if (values.clickedButton === "draft") {
			return yup.object().shape({
				buyer_name: yup.string().when("sub_total", {
					is: (sub_total: number) => sub_total <= 0,
					then: () => yup.string().required("Buyer name is required"),
				}),
				customer_mobile: yup
					.string()
					.matches(PHONE_REGEX, PHONE_REGEX_MESSAGE)
					.nullable(),
				customer_email: yup
					.string()
					.nullable()
					.email("Please enter a valid email"),
				bill_for: yup.string().nullable(),
				buyer_pan: yup
					.string()
					.matches(/^[0-9]*$/, "Please enter a valid number")
					.nullable()
					.when("customer_type", {
						is: (value: string) => value === "business",
						then: () =>
							yup.number().nullable().required("Required"),
					}),
				payment_mode: yup.string().nullable(),
				sub_total: yup
					.number()
					.typeError("Must be a number")
					.required("Required"),
				billed_items: yup.array().when("buyer_name", {
					is: (buyerName: string) =>
						!buyerName || buyerName.trim() === "",
					then: () =>
						yup.array().of(
							yup.object().shape({
								count: yup
									.number()
									.typeError("Must be a number")
									.nullable(),
								item_name: yup
									.string()
									.nullable()
									.max(
										50,
										"Item name must be at most 50 characters"
									)
									.required("Required"),
								item_price: yup
									.number()
									.typeError("Must be a number")
									.nullable()
									.required("Required"),
								item_qty: yup
									.number()
									.typeError("Must be a number")
									.nullable()
									.required("Required"),
								total_price: yup
									.number()
									.typeError("Must be a number")
									.nullable(),
							})
						),
				}),
			});
		} else {
			return yup.object().shape({
				buyer_name: yup.string().required("Buyer name is required"),
				customer_mobile: yup
					.string()
					.matches(PHONE_REGEX, PHONE_REGEX_MESSAGE)
					.nullable(),
				customer_email: yup
					.string()
					.nullable()
					.email("Please enter a valid email"),
				bill_for: yup.string().nullable(),
				buyer_pan: yup
					.string()
					.matches(/^[0-9]*$/, "Please enter a valid number")
					.nullable()
					.when("customer_type", {
						is: (value: string) => value === "business",
						then: () =>
							yup.number().nullable().required("Required"),
					}),
				payment_mode: yup
					.string()
					.nullable()
					.required("Choose Payment mode"),
				sub_total: yup
					.number()
					.typeError("Must be a number")
					.positive("Must be positive value")
					.required("Required"),
				billed_items: yup.array().of(
					yup.object().shape({
						count: yup
							.number()
							.typeError("Must be a number")
							.nullable(),
						item_name: yup
							.string()
							.nullable()
							.max(50, "Item name must be at most 50 characters")
							.required("Required"),
						item_price: yup
							.number()
							.typeError("Must be a number")
							.nullable()
							.required("Required")
							.positive("Required"),
						item_qty: yup
							.number()
							.typeError("Must be a number")
							.nullable()
							.required("Required")
							.positive("Required"),
						total_price: yup
							.number()
							.typeError("Must be a number")
							.nullable(),
					})
				),
			});
		}
	});

	const formik = useFormik<InvoiceFormValues>({
		validationSchema: invoiceSchema,
		initialValues: BLANK_INVOICE_VALUES,
		onSubmit: async (values) => {
			// selectedCustomer's fields are already merged into `values` at
			// selection time (see CustomerSelector's handleCustomerSelection),
			// and the buyer_name/customer_mobile/etc. inputs are disabled
			// while a customer is selected — so values already carries
			// everything the old `...selectedCustomer` spread here duplicated.
			const updatedDraft = sanitizeDraftPayload(props.currentDraft, values);
			await props.draftSave(updatedDraft, values.clickedButton);
			if (props.formType === "bulk") {
				props.onUnsavedChanges({});
			}
		},
	});

	// Stabilized with useCallback: useBillTotals' internal calculateWithTax
	// depends on this reference to decide whether it needs to recompute, so
	// a fresh inline function here (recreated on every InvoiceForm render)
	// caused an infinite render loop — each recompute called
	// formik.setFieldValue, which re-rendered InvoiceForm, which recreated
	// this callback, which looked "changed" to calculateWithTax again.
	// Keyed on props, not on anything InvoiceForm's own state touches, so its
	// own re-renders (formik changes) don't affect this identity.
	const { formType, currentDraft, onUnsavedChanges } = props;
	const handleTotalsChanged = useCallback(
		(nextValues: InvoiceFormValues) => {
			if (formType === "bulk" && !isEmptyObject(currentDraft)) {
				onUnsavedChanges(nextValues);
			}
		},
		[formType, currentDraft, onUnsavedChanges]
	);

	// Bulk mode reports live values via handleTotalsChanged/the explicit
	// resets above, keyed to per-draft bookkeeping in create-invoice.page.tsx.
	// Individual mode (DirectBill) has no such per-draft state — it only
	// needs a plain "is there anything unsaved" signal — so just mirror
	// formik's own dirty flag directly instead. Only depends on `dirty` (not
	// `formik.values`) so this doesn't re-fire on every keystroke; the closed-
	// over values at the moment dirty flips are what DirectBill needs.
	useEffect(() => {
		if (formType === "individual") {
			onUnsavedChanges(formik.dirty ? formik.values : {});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formType, formik.dirty, onUnsavedChanges]);

	const {
		isServiceChargeEnabled,
		setIsServiceChargeEnabled,
		handleDiscountServiceChange,
		handleBilledItemsChange,
		handleServiceChargeToggle,
	} = useBillTotals({
		formik,
		preferenceDetails,
		onTotalsChanged: handleTotalsChanged,
	});

	useEffect(() => {
		// resetForm({values: BLANK_INVOICE_VALUES}), not a bare resetForm():
		// Formik's own `initialValues` ref only updates when resetForm is
		// called *with* a values argument, so a bare call falls back to
		// whatever draft was last explicitly loaded — not an actual blank
		// state. That left the form showing stale draft data after e.g.
		// deleting the currently-open draft or a successful create-save,
		// both of which switch currentDraft to {}.
		formik.resetForm({ values: BLANK_INVOICE_VALUES });
		props.formType === "bulk" && props.onUnsavedChanges({});
		setIsCustomerViewOpen(false);
		setTags([]);

		if (!isEmptyObject(props.currentDraft)) {
			const draftObj = {
				...BLANK_INVOICE_VALUES,
				...props.currentDraft,
				...props.currentDraft?.customer_info,
				buyer_pan:
					props.currentDraft?.buyer_pan ||
					(props.currentDraft?.customer_info as { customer_pan_vat?: string })
						?.customer_pan_vat ||
					null,
				billed_items:
					Array.isArray(props.currentDraft?.billed_items) &&
					(props.currentDraft?.billed_items?.length ?? 0) > 0
						? props.currentDraft?.billed_items
						: [
								{
									item_name: "",
									hs_code: "",
									item_price: 0,
									item_qty: 0,
									total_price: 0,
									vatable:
										!!preferenceDetails?.vat ||
										(preferenceDetails?.esf ?? 0) > 0,
								},
						  ],
			} as InvoiceFormValues;

			// resetForm (not setValues) rebases formik's dirty-comparison
			// baseline to this draft — otherwise dirty would read true the
			// moment this runs whenever currentDraft is non-empty (e.g.
			// DirectBill's default currentInvoiceData), before the user
			// changes anything. See the individual-mode dirty reporting below.
			formik.resetForm({ values: draftObj });
			if (props.currentDraft?.bill_for) {
				setTags(convertTagToArray(props.currentDraft?.bill_for as string));
			}
			if (Number(props.currentDraft?.service_charge) > 0) {
				setIsServiceChargeEnabled(true);
			} else {
				setIsServiceChargeEnabled(false);
			}
		} else {
			setIsServiceChargeEnabled(false);
		}
		// eslint-disable-next-line
	}, [props.currentDraft, preferenceDetails]);

	const handleDraftClick = async (e: React.MouseEvent) => {
		e.preventDefault();
		formik.setFieldValue("clickedButton", "draft");
		await formik.validateForm(); // Validate the form
		formik.submitForm(); // Submit the form
	};

	const handleInvoiceClick = async (e: React.MouseEvent) => {
		e.preventDefault();
		formik.setFieldValue("clickedButton", "invoice");
		await formik.validateForm(); // Validate the form
		// Submitting clears props.currentDraft upstream, which resets this
		// form (see the effect above) — CustomerSelector's own
		// company_customer_id/customer_info effect reactively clears the
		// selected-customer state from that, no explicit clear needed here.
		formik.submitForm(); // Submit the form
	};

	const handleBillClick = async (e: React.MouseEvent) => {
		e.preventDefault();
		if (
			formik.isSubmitting ||
			companyDetails?.company_data_corrupt ||
			!irdStatus
		)
			return;
		formik.setFieldValue("clickedButton", "bill");
		await formik.validateForm(); // Validate the form
		formik.submitForm(); // Submit the form
	};

	const handleIRDStatusCheck = async () => {
		if (irdStatusLoading || companyDetails?.company_data_corrupt) return;
		await checkStatus();
	};

	// duplicate draft function to save in redux
	const handleDraftDuplicate = async (e: React.MouseEvent) => {
		e.preventDefault();
		formik.setFieldValue("clickedButton", "draft");
		const formError = await formik.validateForm(); // Validate the form
		if (isEmptyObject(formError)) {
			const sendingDraft: Record<string, unknown> = {
				...formik.values,
				payment_mode: "credit",
				invoice_number: `DRF_${uuidv4()}`,
				invoice_date: formatEnDate(new Date(), "YYYY-MM-DD"),
				sales_date: formatEnDate(new Date(), "YYYY-MM-DD"),
				draft_valid: false,
			};
			delete sendingDraft.draft_id;
			delete sendingDraft.draft_bill_status;

			addLocalDraft(sendingDraft);
			props.onSetCurrent(sendingDraft);
		} else {
			formik.submitForm(); // to show validation error message
		}
	};

	// Track changes to form input values
	const handleFieldChange = (fieldName: string, fieldValue: unknown) => {
		formik.setFieldValue(fieldName, fieldValue);

		if (props.formType === "bulk" && !isEmptyObject(props.currentDraft)) {
			// Compare the changed value with initial value
			const initialValue = props.currentDraft?.[fieldName];
			if (fieldValue !== initialValue) {
				// the field has changed, so set new formik values to state(unSavedChanges)
				const unsaved = structuredClone({
					...formik.values,
					[fieldName]: fieldValue,
				});
				props.onUnsavedChanges(unsaved);
			} else {
				props.onUnsavedChanges({});
			}
		}
	};

	// Selecting a customer merges their customer_tag into bill_for —
	// CustomerSelector doesn't own the tags UI state, so it hands back the
	// resulting bill_for string for this component to sync into `tags`.
	const handleBillForChange = (billForTag: string) => {
		setTags(convertTagToArray(billForTag));
	};

	// bill for tags functions
	function handleTagAdd(e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>) {
		// Get the value of the input
		const target = e.target as HTMLInputElement;
		const value = target.value;
		// If the value is empty, return
		if (!value.trim()) return;
		// cleaning up all hash values
		const newValue = value?.split("#").filter(Boolean);

		// Convert the arrays to Sets to remove duplicates
		const set1 = new Set(tags);
		const set2 = new Set(newValue);

		// Merge the Sets
		const mergedSet = new Set([...set1, ...set2]);

		// Convert the merged Set back to an array
		const resultArray = Array.from(mergedSet);

		// Add the value to the tags array
		setTags(resultArray);
		// Clear the input
		target.value = "";

		const billForTag =
			Array.isArray(resultArray) && resultArray.length > 0
				? convertTagToString(resultArray)
				: "";
		formik.setFieldValue("bill_for", billForTag);
		if (props.formType === "bulk") {
			// bill for changed so set new values to be stored in redux;
			props.onUnsavedChanges({ ...formik.values, bill_for: billForTag });
		}
	}

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		// If user did not press enter key, return
		if (e.key !== "Enter") return;
		// Now that the form is wrapped in a real <form>, Enter in a text
		// field implicitly submits it — stop that here so Enter in this
		// field only adds a tag, not both adds a tag AND submits the form.
		e.preventDefault();
		handleTagAdd(e);
	}

	function handleTagRemove(index: number) {
		const newTagArray = tags.filter((el, i) => i !== index);
		setTags(newTagArray);

		const billForTag =
			Array.isArray(newTagArray) && newTagArray.length > 0
				? convertTagToString(newTagArray)
				: "";
		formik.setFieldValue("bill_for", billForTag);
		if (props.formType === "bulk") {
			// bill for changed so set new values to be stored in redux;
			props.onUnsavedChanges({ ...formik.values, bill_for: billForTag });
		}
	}

	return (
		<form
			className="invoice-form-container"
			onSubmit={(e) => e.preventDefault()}
		>
				<div className="bill-header-container">
					<div className="bill-details-container">
						<div className="row-item">
							<div className="label-title">Invoice Details</div>
							<div className="invoice-date-notice">
								<InlineNotice
									message="Invoice date is always set to today when the bill is submitted — it can't be backdated. Use Sales Date below for the actual transaction date."
									type="info"
								/>
							</div>
							<div className="input-row-wrapper">
								<div className="input-wrapper">
									<label className="input-label">
										Invoice Date
									</label>
									{/* Always today, not formik.values.invoice_date — the server stamps invoice_date as "now" at bill creation, ignoring any stored value. */}
									<InputDatePicker value={new Date()} disabled />
								</div>

								<div className="input-wrapper">
									<label className="input-label">
										Sales Date
									</label>
									<InputDatePicker
										value={
											formik.values.sales_date
												? new Date(formik.values.sales_date)
												: null
										}
										onChange={(result) =>
											handleFieldChange(
												"sales_date",
												formatEnDate(result.en, "YYYY-MM-DD"),
											)
										}
										maxDate={new Date()}
										clearable
										onClear={() => handleFieldChange("sales_date", "")}
										error={
											formik.submitCount > 0
												? (formik.errors as FormikErrors<InvoiceFormValues>)
														.sales_date
												: undefined
										}
									/>
								</div>

								{props.currentDraft?.draft_bill_status ? (
									<div className="input-wrapper">
										<label className="input-label">
											Invoice Number #
										</label>
										<input
											name="invoice_number"
											className="input-box-wrapper"
											style={{ textAlign: "right" }}
											type="text"
											placeholder="Invoice Number #"
											value={
												formik.values?.invoice_number ||
												""
											}
											disabled={true}
										/>
										{formik.submitCount > 0 && (
											<div className="validation-error">
												{formik.errors.invoice_number}
											</div>
										)}
									</div>
								) : null}
							</div>
						</div>
					</div>

					<CustomerSelector
						formik={formik}
						formType={props.formType}
						onFieldChange={handleFieldChange}
						onUnsavedChanges={props.onUnsavedChanges}
						onBillForChange={handleBillForChange}
						isCustomerViewOpen={isCustomerViewOpen}
						onCustomerViewChange={setIsCustomerViewOpen}
					/>

					<div className="bill-details-container">
						<div className="row-item">
							<div className="label-title">Bill For</div>
							<MultipleTagsInput
								label="Customer Tags"
								name="bill_for"
								onKeyDown={handleKeyDown}
								onBlur={handleTagAdd}
								onTagRemove={handleTagRemove}
								tagsArray={tags}
								autoComplete="off"
							/>
						</div>
						<div className="row-item">
							<div className="label-title">Payment Method</div>
							<div className="input-item">
								<p className="input-label">Mode of Payment</p>
								<SelectBox
									name="payment_mode"
									onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
										handleFieldChange(
											"payment_mode",
											e.target.value
										)
									}
									onBlur={formik.handleBlur}
									value={formik.values?.payment_mode || ""}
									options={strings.paymentMode}
								/>
								{formik.submitCount > 0 && (
									<div className="validation-error">
										{formik?.errors?.payment_mode}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<InvoiceItemsFooter
					formik={formik}
					onDiscountServiceChange={handleDiscountServiceChange}
					onBilledItemsChange={handleBilledItemsChange}
					onServiceChargeToggle={handleServiceChargeToggle}
					isServiceChargeEnabled={isServiceChargeEnabled}
					onFieldValueChange={handleFieldChange}
					preferenceDetails={preferenceDetails}
				/>
				<div className="divider"></div>

				<div className="invoice-actions">
					{companyDetails?.company_data_corrupt ? (
						<div className="invoice-actions-warn">
							There are
							<span style={{ fontWeight: "bold" }}>
								{" "}
								technical errors with your IRD account.{" "}
							</span>
							Bills cannot be created. Please contact your service
							providers to resolve this issue.
						</div>
					) : (
						!irdStatus && (
							<div className="invoice-actions-warn">
								Bill generation is currently not possible due to
								an error with the IRD server. Please check the
								IRD status and try again later.
							</div>
						)
					)}

					<div className="invoice-form-btns">
						{props.formType === "individual" ? (
							<>
								{!irdStatus && (
									<button
										type="button"
										className="form-submit-btn"
										onClick={handleIRDStatusCheck}
										disabled={
											irdStatusLoading ||
											companyDetails?.company_data_corrupt
										}
									>
										{irdStatusLoading ? (
											<>
												<Spinner styles={undefined} />{" "}
												<span>Checking...</span>
											</>
										) : (
											<>
												<FaPaperPlane />
												<span>Check IRD Status</span>
											</>
										)}
									</button>
								)}
								{props.currentDraft?.draft_bill_status ===
								"failed" ? (
									<button
										className="form-submit-btn inverted"
										type="submit"
										onClick={(e) => handleInvoiceClick(e)}
										disabled={formik.isSubmitting}
									>
										<FaFloppyDisk />
										Ready Draft
									</button>
								) : (
									<button
										className="form-submit-btn inverted"
										type="submit"
										onClick={(e) => handleBillClick(e)}
										disabled={
											formik.isSubmitting ||
											companyDetails?.company_data_corrupt ||
											!irdStatus
										}
									>
										{formik.isSubmitting ? (
											<>
												<Spinner styles={undefined} />{" "}
												<span>Creating...</span>
											</>
										) : (
											<>
												<FaPaperPlane />
												<span>Create Bill</span>
											</>
										)}
									</button>
								)}
							</>
						) : (
							<>
								<button
									type="button"
									className="form-submit-btn"
									onClick={(e) => handleDraftDuplicate(e)}
									disabled={formik.isSubmitting}
								>
									<FaCopy />
									Duplicate
								</button>
								<button
									className="form-submit-btn"
									type="submit"
									onClick={(e) => handleDraftClick(e)}
									disabled={formik.isSubmitting}
								>
									<FaFloppyDisk />
									Save Draft
								</button>
								<button
									className="form-submit-btn inverted"
									type="submit"
									onClick={(e) => handleInvoiceClick(e)}
									disabled={formik.isSubmitting}
								>
									<FaFloppyDisk />
									Ready Draft
								</button>
							</>
						)}
					</div>
				</div>
		</form>
	);
}

export default memo(InvoiceForm);
